import axios from "axios";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as THREE from "three";

import {
  setDaylightBoxView,
  setDaylightIsLoading,
  updateDaylightConfigurationPosition,
} from "../../redux/daylightFactor";
import {
  daylightFactorScale,
  getRangeModeDisplayColor,
  getPolygonCentroid,
} from "../utils";
import { DEFAULT_COLORBAR_RANGE_MODE } from "../colorBarComponent/colorbarConstants";
import { isOptionSelected } from "../utility/helpers";
import { reduxLocalStorage } from "../utility/storage";
import { ValueLabelsGroup } from "./ValueLabelsGroup";
import { HEATMAP_LABEL_LAYER } from "./heatmapLabelLayers";
function genratePolygon(data) {
  var output = [];
  data.forEach((k) => {
    output.push(new THREE.Vector3(k[0], k[1], k[2]));
  });
  return output;
}

const DaylightFactor = ({}) => {
  const dispatch = useDispatch();
  const selectedFloor = useSelector(
    (state) => state.floorVisibility.selectedFloors,
  );
  const [resultArray, setResultArray] = useState();
  const [nodes, setNodes] = useState(false);

  const project_id = reduxLocalStorage.getItem("project_id");
  const token = localStorage.getItem("token");
  const getLocalStorageItem = (key) => {
    const item = reduxLocalStorage.getItem(key);
    return item ? item.split(",") : [];
  };

  const floorsList = getLocalStorageItem("floor_list");
  const scenarioList = getLocalStorageItem("daylight_scenario_list");
  const valueList = getLocalStorageItem("daylight_value_list");

  var selector = useSelector((state) => state.daylightFactor.value);
  const isDaylightSelected = selector?.parentName !== "";
  let allURLs = [];

  floorsList.forEach((floorVal) => {
    allURLs.push(
      `${process.env.REACT_APP_API_URL}api/daylight/?project_id=${project_id}&floor=${floorVal}`,
    );
  });
  var responses = [];
  useEffect(() => {
    if (isDaylightSelected && !nodes) {
      dispatch(setDaylightIsLoading(true));
      axios
        .all(
          allURLs.map((endpoint) =>
            axios.get(endpoint, {
              headers: {
                "Content-Type": "application/json; charset=UTF-8",
                Authorization: `Bearer ${token}`,
              },
            }),
          ),
        )
        .then((data) => {
          setNodes(true);
          data.forEach((val) => {
            responses.push(val.data);
          });
          setResultArray(responses);
        })
        .finally(() => {
          dispatch(setDaylightIsLoading(false));
        });
    }
  }, [isDaylightSelected]);

  const colorbars = useSelector((state) => state.colorbars["Daylight Factor"]);

  if (resultArray?.length > 0)
    return (
      <>
        {resultArray.map((eachFloor, floorIndex) => {
          return valueList.map((value, valueIndex) => {
            const {
              max,
              min,
              rangeMode = DEFAULT_COLORBAR_RANGE_MODE,
            } = colorbars[value] || { max: 100, min: 0 };
            return scenarioList.map((scenario, scenarioIndex) => {
              const parentKey = `${floorIndex}-${valueIndex}-${scenarioIndex}`;
              const isLayerVisible =
                selectedFloor[floorIndex] &&
                isOptionSelected({
                  selector,
                  parentName: value,
                  childName: scenario,
                });
              const getRoomVisible = (room) => {
                if (!isLayerVisible) return false;
                if (!room?.values || room?.values === "") return false;

                const roomValue = JSON.parse(room?.values)?.[valueIndex]?.[
                  value
                ]?.[scenarioIndex];
                const { visible: valueVisible } = getRangeModeDisplayColor(
                  roomValue,
                  {
                    min,
                    max,
                    rangeMode,
                    colorScale: daylightFactorScale,
                  },
                );

                return valueVisible;
              };
              const getRoomColor = (room) => {
                if (!room?.values || room?.values === "") return null;
                const roomValue = JSON.parse(room?.values)?.[valueIndex]?.[
                  value
                ]?.[scenarioIndex];
                return getRangeModeDisplayColor(roomValue, {
                  min,
                  max,
                  rangeMode,
                  colorScale: daylightFactorScale,
                }).color;
              };
              return (
                <group rotation-x={Math.PI * -0.5} key={parentKey}>
                  {eachFloor.map((room, i) => {
                    const isVisible = isLayerVisible;
                    if (!room?.values || room?.values == "") room.values = "[]";

                    const roomValue = JSON.parse(room?.values)?.[valueIndex]?.[
                      value
                    ]?.[scenarioIndex];
                    const roomPolygon = JSON.parse(room.room_polygon);
                    const roomZ = roomPolygon[0][2];
                    const { visible: valueVisible, color: meshColor } =
                      getRangeModeDisplayColor(roomValue, {
                        min,
                        max,
                        rangeMode,
                        colorScale: daylightFactorScale,
                      });

                    return (
                      <mesh
                        key={`${parentKey}-${i}`}
                        userData={{ excludeFromLabelOcclusion: true }}
                        position={[0, 0, roomZ]}
                        visible={isVisible && valueVisible}
                        onClick={(config) => {
                          if (isVisible) {
                            dispatch(setDaylightBoxView(true));
                            dispatch(
                              updateDaylightConfigurationPosition([
                                config.pageX,
                                config.pageY,
                                room.text,
                                room.room_area,
                                room.room_polygon,
                                JSON.parse(room?.values)?.[valueIndex]?.[value],
                              ]),
                            );
                            config.stopPropagation();
                          }
                        }}
                      >
                        <primitive
                          object={
                            new THREE.ShapeGeometry(
                              new THREE.Shape(genratePolygon(roomPolygon)),
                            )
                          }
                          attach={"geometry"}
                        />

                        <meshBasicMaterial
                          side={THREE.DoubleSide}
                          color={meshColor}
                        />
                      </mesh>
                    );
                  })}
                  {isLayerVisible && (
                    <ValueLabelsGroup
                      visible={isLayerVisible}
                      layerKey={HEATMAP_LABEL_LAYER.DAYLIGHT}
                      items={eachFloor}
                      getVisible={getRoomVisible}
                      getPosition={(room) => {
                        const roomPolygon = JSON.parse(room.room_polygon);
                        const [cx, cy] = getPolygonCentroid(roomPolygon);
                        const roomZ = roomPolygon[0][2];
                        return [cx, cy, roomZ];
                      }}
                      getValue={(room) => {
                        if (!room?.values || room?.values == "") return null;
                        return JSON.parse(room?.values)?.[valueIndex]?.[
                          value
                        ]?.[scenarioIndex];
                      }}
                      getLabelPolygon={(room) => JSON.parse(room.room_polygon)}
                      getColor={getRoomColor}
                    />
                  )}
                </group>
              );
            });
          });
        })}
      </>
    );
};

export default DaylightFactor;
